import { useEffect, useState } from "react";
import { apiClient } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Ticket, Plus, Trash2, Pencil, Copy, RefreshCw, ShieldCheck, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "../components/ConfirmDialog";

const emptyForm = {
  code: "",
  description: "",
  discount_type: "percent",
  discount_value: 20,
  applicable_plans: [],
  billing_cycle: "any",
  duration_type: "first_period",
  duration_periods: 0,
  max_redemptions: 0,
  valid_from: "",
  valid_until: "",
  is_active: true,
  stripe_coupon_id: "",
};

export default function AdminDiscounts() {
  const [codes, setCodes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [resyncingId, setResyncingId] = useState(null);

  const fetchAll = async () => {
    try {
      const [c, p] = await Promise.all([
        apiClient.get("/admin/discount-codes"),
        apiClient.get("/admin/plans"),
      ]);
      setCodes(c.data.codes || []);
      setPlans(p.data.plans || []);
    } catch {
      toast.error("Failed to load discount codes");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c });
    setDialogOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip UI-only fields (prefixed with _) before sending to backend
      const cleanForm = Object.fromEntries(
        Object.entries(form).filter(([k]) => !k.startsWith("_"))
      );
      if (editing) {
        const { code: _skip, ...patch } = cleanForm;
        await apiClient.patch(`/admin/discount-codes/${editing.code_id}`, patch);
        toast.success("Code updated");
      } else {
        await apiClient.post("/admin/discount-codes", cleanForm);
        toast.success("Code created");
      }
      setDialogOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete "${c.code}"?`,
      description: "This permanently deletes the discount code from Nirvanta and archives the linked Stripe coupon. Existing customers on this code are unaffected; new customers will get an \"invalid code\" error. This cannot be undone.",
      confirmText: "Delete code",
      variant: "danger",
    });
    if (!ok) return;
    try { await apiClient.delete(`/admin/discount-codes/${c.code_id}`); toast.success("Deleted"); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const copyLink = (c) => {
    const url = `${window.location.origin}/signup?code=${encodeURIComponent(c.code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Signup link copied");
  };

  const togglePlan = (planId) => {
    const set = new Set(form.applicable_plans || []);
    if (set.has(planId)) set.delete(planId); else set.add(planId);
    setForm({ ...form, applicable_plans: Array.from(set) });
  };

  const formatDiscount = (c) => c.discount_type === "percent" ? `${c.discount_value}%` : `$${(c.discount_value / 100).toFixed(2)}`;
  const formatDuration = (c) => {
    if (c.duration_type === "forever") return "Recurring forever";
    if (c.duration_type === "fixed_periods") return `${c.duration_periods} periods`;
    return "First period only";
  };

  // Stripe sync health — we look up each code in the last audit report to
  // decide what badge to show. Codes not present in the audit fall back to
  // a simple "has_promo_id" check.
  const stripeStatusFor = (c) => {
    if (auditReport) {
      if (auditReport.report.synced.find((r) => r.code_id === c.code_id)) return "synced";
      if (auditReport.report.out_of_sync.find((r) => r.code_id === c.code_id)) return "out_of_sync";
      if (auditReport.report.errored.find((r) => r.code_id === c.code_id)) return "errored";
      if (auditReport.report.not_synced.find((r) => r.code_id === c.code_id)) return "not_synced";
    }
    return (c.stripe_coupon_id && c.stripe_promotion_code_id) ? "assumed_synced" : "not_synced";
  };
  const stripeDriftFor = (c) => {
    if (!auditReport) return null;
    const row = auditReport.report.out_of_sync.find((r) => r.code_id === c.code_id)
             || auditReport.report.errored.find((r) => r.code_id === c.code_id);
    return row ? (row.drift || [row.error]) : null;
  };

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await apiClient.post("/admin/discount-codes/audit");
      setAuditReport(res.data);
      const s = res.data.summary || {};
      const problems = (s.out_of_sync || 0) + (s.errored || 0) + (s.not_synced || 0);
      if (problems === 0) toast.success(`All ${s.synced} codes are in sync with Stripe`);
      else toast.warning(`${problems} code(s) need attention — see the Stripe column`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audit failed");
    } finally { setAuditLoading(false); }
  };

  const forceResync = async (c) => {
    const ok = await confirm({
      title: `Force re-sync "${c.code}" to Stripe?`,
      description: "This archives the existing Stripe coupon and creates a new one from the current Nirvanta config. Anyone who has a bookmarked checkout URL for this code will need to start a fresh checkout.",
      confirmText: "Re-sync now",
      variant: "resync",
    });
    if (!ok) return;
    setResyncingId(c.code_id);
    try {
      await apiClient.post(`/admin/discount-codes/${c.code_id}/resync`);
      toast.success(`Re-synced "${c.code}" to Stripe`);
      await fetchAll();
      if (auditReport) await runAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resync failed");
    } finally { setResyncingId(null); }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl space-y-6" data-testid="admin-discounts-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight flex items-center gap-2">
              <Ticket className="w-7 h-7 text-rose-500" /> Discount Codes
            </h1>
            <p className="text-slate-600 mt-1">
              Create promo codes for trials, launch sales, or one-off customer deals.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runAudit}
              disabled={auditLoading}
              variant="outline"
              data-testid="admin-discounts-audit-btn"
              className="border-slate-300"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {auditLoading ? "Auditing…" : "Audit Stripe sync"}
            </Button>
            <Button onClick={openCreate} className="bg-[#0D9488] hover:bg-[#14B8A6]"
              data-testid="admin-discounts-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Code
            </Button>
          </div>
        </div>

        {auditReport && (
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm flex flex-wrap items-center gap-4"
            data-testid="audit-summary-banner"
          >
            <span className="font-semibold text-slate-700">Last audit:</span>
            <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {auditReport.summary.synced} synced</span>
            <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-4 h-4" /> {auditReport.summary.out_of_sync} out-of-sync</span>
            <span className="flex items-center gap-1 text-rose-700"><XCircle className="w-4 h-4" /> {auditReport.summary.errored} errored</span>
            <span className="flex items-center gap-1 text-slate-500">{auditReport.summary.not_synced} not synced</span>
            {(auditReport.summary.out_of_sync + auditReport.summary.errored + auditReport.summary.not_synced) > 0 && (
              <span className="text-xs text-slate-500 ml-auto">Click the ↻ icon on any row to force-resync.</span>
            )}
          </div>
        )}

        <Card>
          <CardHeader><CardTitle className="text-lg">All Codes</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-32 bg-slate-100 rounded animate-pulse" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Applies to</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Tenants applied</TableHead>
                    <TableHead>Valid until</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stripe</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-discounts-table-body">
                  {codes.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-8">
                      No discount codes yet.
                    </TableCell></TableRow>
                  )}
                  {codes.map((c) => (
                    <TableRow key={c.code_id} data-testid={`admin-discount-row-${c.code_id}`}>
                      <TableCell>
                        <div className="font-mono font-bold text-[#0A192F]">{c.code}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{c.description}</div>
                      </TableCell>
                      <TableCell><span className="font-semibold text-rose-600">{formatDiscount(c)}</span> off</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatDuration(c)}</TableCell>
                      <TableCell className="text-sm">
                        {c.applicable_plans?.length ? c.applicable_plans.join(", ") : <em>All plans</em>}
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                          {c.billing_cycle === "any" ? "monthly + annual" : c.billing_cycle}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {c.redemption_count}
                        {c.max_redemptions > 0 && ` / ${c.max_redemptions}`}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(c.tenants_using_count || 0) === 0 ? (
                          <span className="text-slate-400 text-xs">—</span>
                        ) : (
                          <div
                            className="max-w-[180px]"
                            title={(c.tenants_using || []).map(t => `${t.company_name} (${t.applied_at ? new Date(t.applied_at).toLocaleDateString() : ""})`).join("\n")}
                            data-testid={`tenants-using-${c.code_id}`}
                          >
                            <span className="inline-flex items-center gap-1 text-teal-800 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 text-[11px] font-semibold">
                              {c.tenants_using_count}
                            </span>
                            <div className="text-[11px] text-slate-600 truncate mt-0.5">
                              {(c.tenants_using || []).slice(0, 2).map(t => t.company_name).join(", ")}
                              {c.tenants_using_count > 2 && ` +${c.tenants_using_count - 2}`}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${c.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {c.is_active ? "Active" : "Disabled"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const st = stripeStatusFor(c);
                          const drift = stripeDriftFor(c);
                          const map = {
                            synced:          { icon: CheckCircle2,   cls: "bg-emerald-50 text-emerald-700", label: "Synced" },
                            assumed_synced:  { icon: CheckCircle2,   cls: "bg-slate-50 text-slate-600",    label: "Synced?" },
                            out_of_sync:     { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700",    label: "Out of sync" },
                            errored:         { icon: XCircle,        cls: "bg-rose-50 text-rose-700",       label: "Error" },
                            not_synced:      { icon: AlertTriangle, cls: "bg-slate-100 text-slate-600",   label: "Not synced" },
                          };
                          const meta = map[st];
                          const Icon = meta.icon;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${meta.cls}`}
                              title={drift ? drift.join("\n") : ""}
                              data-testid={`stripe-status-${c.code_id}`}
                            >
                              <Icon className="w-3 h-3" /> {meta.label}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => forceResync(c)}
                            disabled={resyncingId === c.code_id}
                            data-testid={`admin-discount-resync-${c.code_id}`}
                            className="text-slate-500 hover:text-amber-600" aria-label="Force resync to Stripe">
                            <RefreshCw className={`w-4 h-4 ${resyncingId === c.code_id ? "animate-spin" : ""}`} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => copyLink(c)}
                            data-testid={`admin-discount-copy-${c.code_id}`}
                            className="text-slate-500 hover:text-blue-600" aria-label="Copy link"><Copy className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                            data-testid={`admin-discount-edit-${c.code_id}`}
                            className="text-slate-500 hover:text-[#0D9488]" aria-label="Edit"><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(c)}
                            data-testid={`admin-discount-delete-${c.code_id}`}
                            className="text-rose-500 hover:text-rose-700" aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-discount-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Discount Code" : "New Discount Code"}</DialogTitle>
            <DialogDescription>Percent discounts are 1–100. Fixed amount discounts are in USD dollars (e.g. 5 or 5.00 for $5 off).</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code</Label>
                <Input value={form.code} disabled={!!editing}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })}
                  placeholder="LAUNCH50" data-testid="admin-discount-code-input" required />
              </div>
              <div>
                <Label>Discount type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="admin-discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="amount">Fixed amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{form.discount_type === "percent" ? "Percent off (1-100)" : "Amount off (USD)"}</Label>
              <Input
                type="number"
                min={form.discount_type === "percent" ? 1 : 0}
                max={form.discount_type === "percent" ? 100 : undefined}
                step={form.discount_type === "percent" ? 1 : "0.01"}
                value={
                  form.discount_type === "percent"
                    ? form.discount_value
                    : (form._amount_dollars ?? (form.discount_value / 100).toString())
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  if (form.discount_type === "percent") {
                    setForm({ ...form, discount_value: Number(raw) || 1 });
                  } else {
                    const cents = Math.round((parseFloat(raw) || 0) * 100);
                    setForm({ ...form, _amount_dollars: raw, discount_value: cents });
                  }
                }}
                data-testid="admin-discount-value-input"
                placeholder={form.discount_type === "percent" ? "20" : "5.00"}
                required
              />
              {form.discount_type === "amount" && (
                <p className="text-xs text-slate-500 mt-1">${(form.discount_value / 100).toFixed(2)} off</p>
              )}
            </div>
            <div>
              <Label>Description (internal only)</Label>
              <Input value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="For John from ACME Corp" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration</Label>
                <Select value={form.duration_type} onValueChange={(v) => setForm({ ...form, duration_type: v })}>
                  <SelectTrigger data-testid="admin-discount-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_period">First period only</SelectItem>
                    <SelectItem value="fixed_periods">Fixed N periods</SelectItem>
                    <SelectItem value="forever">Recurring forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.duration_type === "fixed_periods" && (
                <div>
                  <Label>Number of periods</Label>
                  <Input type="number" min={1} value={form.duration_periods}
                    onChange={(e) => setForm({ ...form, duration_periods: Number(e.target.value) || 1 })} />
                </div>
              )}
              <div>
                <Label>Billing cycle</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger data-testid="admin-discount-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any (monthly + annual)</SelectItem>
                    <SelectItem value="monthly">Monthly only</SelectItem>
                    <SelectItem value="annual">Annual only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Applicable plans</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {plans.map((p) => {
                  const on = form.applicable_plans?.includes(p.plan_id);
                  return (
                    <button key={p.plan_id} type="button" onClick={() => togglePlan(p.plan_id)}
                      className={`px-3 py-1 rounded-full text-sm border ${on ? "bg-[#0D9488] text-white border-[#0D9488]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                      {p.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave empty to apply to all plans.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max redemptions (0 = unlimited)</Label>
                <Input type="number" min={0} value={form.max_redemptions}
                  onChange={(e) => setForm({ ...form, max_redemptions: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Valid until</Label>
                <Input type="date"
                  value={form.valid_until ? form.valid_until.slice(0, 10) : ""}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0D9488] hover:bg-[#14B8A6]"
                data-testid="admin-discount-save-btn">
                {saving ? "Saving..." : editing ? "Save Changes" : "Create Code"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
